import { Star, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#FF69B4]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">Terms & Conditions</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
          <h1 className="font-outfit font-bold text-2xl md:text-3xl text-[#1E293B] mb-6">Terms and Conditions</h1>
          <p className="text-sm text-[#64748B] mb-6">Last updated: December 2025</p>

          <div className="space-y-6 text-[#475569]">
            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using FOUNDSTAR ("Platform"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our Platform.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">2. Definitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>"Platform"</strong> refers to FOUNDSTAR website and mobile applications.</li>
                <li><strong>"User"</strong> refers to any person who accesses or uses the Platform.</li>
                <li><strong>"Influencer"</strong> refers to content creators registered on our Platform.</li>
                <li><strong>"Brand"</strong> refers to businesses or individuals seeking to collaborate with influencers.</li>
                <li><strong>"Collaboration"</strong> refers to any business arrangement between a Brand and an Influencer facilitated through our Platform.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">3. Eligibility</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>You must be at least 18 years old to use this Platform.</li>
                <li>You must provide accurate and complete information during registration.</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">4. User Accounts</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>You may register as either an Influencer or a Brand.</li>
                <li>You must not create multiple accounts or impersonate others.</li>
                <li>You are responsible for all activities that occur under your account.</li>
                <li>We reserve the right to suspend or terminate accounts that violate these terms.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">5. Influencer Verification</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Influencers may apply for verified status by paying a verification fee.</li>
                <li>Verification is subject to approval by our team.</li>
                <li>We reserve the right to reject verification requests without providing reasons.</li>
                <li>Verified status may be revoked if terms are violated.</li>
                <li>Providing false information during verification will result in permanent ban.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">6. Collaborations</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Brands may send collaboration requests to Influencers through the Platform.</li>
                <li>Influencers may accept or reject collaboration requests at their discretion.</li>
                <li>All collaboration terms should be clearly communicated through our messaging system.</li>
                <li>FOUNDSTAR is not responsible for the quality or outcome of collaborations.</li>
                <li>Both parties must honor agreed-upon terms and deadlines.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">7. Payments</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Payments are processed through our secure payment partner (Razorpay).</li>
                <li>FOUNDSTAR charges a platform commission on successful collaborations.</li>
                <li>Funds are held in escrow until collaboration terms are fulfilled.</li>
                <li>Refunds are subject to our Cancellation and Refund Policy.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">8. Prohibited Activities</h2>
              <p className="mb-3">Users must not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Share contact information to conduct deals outside the Platform</li>
                <li>Post false, misleading, or fraudulent content</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Use the Platform for illegal activities</li>
                <li>Attempt to circumvent platform fees</li>
                <li>Use bots or automated systems to access the Platform</li>
                <li>Infringe on intellectual property rights</li>
                <li>Manipulate follower counts or engagement metrics</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">9. Content Guidelines</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Users are responsible for content they post on the Platform.</li>
                <li>Content must not be offensive, obscene, or discriminatory.</li>
                <li>Content must comply with applicable laws and regulations.</li>
                <li>We reserve the right to remove content that violates these terms.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">10. Intellectual Property</h2>
              <p>
                All content, trademarks, and intellectual property on the Platform belong to FOUNDSTAR or its licensors. Users may not copy, modify, or distribute our content without permission.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">11. Limitation of Liability</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>FOUNDSTAR is a platform that facilitates connections and is not party to collaborations.</li>
                <li>We are not liable for disputes between Brands and Influencers.</li>
                <li>We are not responsible for losses arising from use of the Platform.</li>
                <li>Our liability is limited to the amount paid to us in the preceding 12 months.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">12. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless FOUNDSTAR, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the Platform or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">13. Dispute Resolution</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Any disputes shall be resolved through arbitration in accordance with Indian law.</li>
                <li>The arbitration shall be conducted in Mumbai, India.</li>
                <li>Users agree to attempt mediation before pursuing arbitration.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">14. Modifications</h2>
              <p>
                We reserve the right to modify these Terms and Conditions at any time. Changes will be effective upon posting on the Platform. Continued use of the Platform constitutes acceptance of modified terms.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">15. Governing Law</h2>
              <p>
                These Terms and Conditions are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Mumbai, Maharashtra.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">16. Contact Us</h2>
              <p>
                For any questions regarding these Terms and Conditions, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Email:</strong> <a href="mailto:support@foundstar.in" className="text-[#FF69B4] hover:underline">support@foundstar.in</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsConditions;
